import { Link } from "react-router-dom";
import Logo from "../components/Logo";
import QuickLoginButton from "../components/QuickLoginButton";
import {
    Sparkles, ShieldCheck, Zap, Building2, User, Briefcase,
    CreditCard, ShoppingBag, Wallet, Truck, Sprout, FileText, Receipt,
    Package, Users as UsersIcon, BarChart3, Layers,
} from "lucide-react";
import { Button } from "../components/ui/button";

const CONSUMER_MOCKUP =
    "https://customer-assets.emergentagent.com/job_bd924cda-aeef-4b77-b265-df028a44b434/artifacts/etlpj3ft_8437A891-256C-4917-9F6D-DCF87A317323.PNG";
const DETAILED_MOCKUP =
    "https://customer-assets.emergentagent.com/job_bd924cda-aeef-4b77-b265-df028a44b434/artifacts/wyvfz4xa_AC0F5579-2552-41A4-AC65-FA3DA1FAC6CC.PNG";

const FEATURES = [
    { icon: Sparkles, label: "Credit DNA Score™", desc: "AI underwriting tuned for African flows.", color: "#7000FF" },
    { icon: Zap, label: "Instant Disbursement", desc: "Funds in your wallet in under 60 seconds.", color: "#00D084" },
    { icon: ShieldCheck, label: "Bank-grade Security", desc: "Encrypted, PCI-ready, fraud-aware by design.", color: "#0066FF" },
];

const CONSUMER_PRODUCTS = [
    { name: "NanoCredit™", desc: "Instant micro loans up to ₦50,000", icon: Zap, color: "#00D084" },
    { name: "FlexCredit™", desc: "Revolving credit line up to ₦250,000", icon: CreditCard, color: "#0066FF" },
    { name: "SalaryAdvance™", desc: "Up to 50% of salary, paid back on payday", icon: Wallet, color: "#FF6B00" },
    { name: "SplitPay™", desc: "Borrow together, build together — 2–6 installments", icon: ShoppingBag, color: "#7000FF" },
    { name: "PrimeCredit™", desc: "Larger loans with better rates, up to ₦5M", icon: Sparkles, color: "#FF0055" },
];

const BUSINESS_PRODUCTS = [
    { name: "BizCredit™", desc: "Working capital up to ₦5,000,000", icon: Briefcase, color: "#0066FF" },
    { name: "TradeCredit™", desc: "Invoice & supplier financing up to ₦10M", icon: FileText, color: "#06B6D4" },
    { name: "StockFinance™", desc: "Inventory-backed lending up to ₦7.5M", icon: Package, color: "#FF6B00" },
    { name: "AgroCredit™", desc: "Satellite-driven farm finance up to ₦2M", icon: Sprout, color: "#00D084" },
    { name: "FleetFinance™", desc: "GPS-driven logistics credit up to ₦5M", icon: Truck, color: "#7000FF" },
];

const MERCHANT_TOOLS = [
    { icon: ShoppingBag, label: "POS Sales" },
    { icon: Package, label: "Inventory" },
    { icon: UsersIcon, label: "Customers" },
    { icon: Briefcase, label: "Suppliers" },
    { icon: Receipt, label: "Expenses" },
    { icon: FileText, label: "Invoices" },
    { icon: BarChart3, label: "Reports" },
    { icon: Layers, label: "MerchantOS™" },
];

const ADMIN_TOOLS = [
    "Portfolio dashboard",
    "Users & businesses management",
    "Loan approvals & decisions",
    "Risk & fraud signals",
    "Collections workflow",
    "Credit Engine rule editor",
    "Transactions ledger",
    "System logs",
];

export default function LandingPage() {
    return (
        <div className="min-h-screen bg-white">
            {/* Top bar */}
            <header className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
                <Logo size={36} />
                <div className="flex items-center gap-2">
                    <Link to="/login" className="hidden md:inline-block" data-testid="header-try-demo-link">
                        <Button variant="ghost" className="font-semibold" data-testid="header-try-demo">Try the demo</Button>
                    </Link>
                    <Link to="/login" data-testid="landing-login-link">
                        <Button variant="ghost" className="font-semibold">Sign in</Button>
                    </Link>
                    <Link to="/register" data-testid="landing-register-link">
                        <Button className="bg-[#0A1930] hover:bg-[#0d1f3d] font-semibold">Get started</Button>
                    </Link>
                </div>
            </header>

            {/* Hero */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 lg:pt-20 pb-14 grid lg:grid-cols-2 gap-12 items-center">
                <div>
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-slate-100 text-slate-700">
                        <Sparkles size={12} className="text-[#7000FF]" /> Africa's Credit OS
                    </span>
                    <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05] text-[#0A1930]">
                        Smart Credit.{" "}
                        <span className="gradient-rainbow-text">Simple Process.</span>{" "}
                        Stronger Personal Finance.{" "}
                        <span className="gradient-rainbow-text">Stronger Business.</span>
                    </h1>
                    <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
                        OnePassCredit is the unified financial operating system for African consumers and MSMEs —
                        instant lending, embedded payments, AI underwriting, and merchant tools in one app.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <Link to="/register?role=consumer" data-testid="hero-cta-consumer">
                            <Button className="bg-[#7000FF] hover:bg-[#5d00d6] text-white font-semibold rounded-xl px-6 h-12">
                                <User size={18} /> Individual app
                            </Button>
                        </Link>
                        <Link to="/register?role=business" data-testid="hero-cta-business">
                            <Button className="bg-[#0066FF] hover:bg-[#0052cc] text-white font-semibold rounded-xl px-6 h-12">
                                <Briefcase size={18} /> Business app
                            </Button>
                        </Link>
                        <Link to="/login" data-testid="hero-cta-demo">
                            <Button variant="outline" className="font-semibold rounded-xl px-6 h-12 border-slate-300">
                                <Building2 size={18} /> Try the demo
                            </Button>
                        </Link>
                    </div>
                </div>

                {/* Hero mockup — show all three apps as actual mockup screenshot */}
                <div className="relative">
                    <div className="absolute -inset-10 bg-gradient-to-tr from-[#7000FF]/20 via-[#0066FF]/15 to-[#00D084]/20 rounded-[3rem] blur-3xl pointer-events-none" />
                    <img
                        src={CONSUMER_MOCKUP}
                        alt="OnePassCredit apps — Individual, Business, Admin"
                        className="relative w-full rounded-3xl shadow-2xl"
                        data-testid="hero-mockup"
                    />
                </div>
            </section>

            {/* Features ribbon */}
            <section className="bg-slate-50 border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid md:grid-cols-3 gap-6">
                    {FEATURES.map((f, i) => (
                        <div
                            key={f.label}
                            className="bg-white p-6 rounded-2xl border border-slate-200 product-shadow"
                            data-testid={`feature-${i}`}
                        >
                            <div
                                className="size-11 rounded-xl flex items-center justify-center"
                                style={{ background: `${f.color}15` }}
                            >
                                <f.icon size={22} style={{ color: f.color }} />
                            </div>
                            <h3 className="mt-4 font-display font-semibold text-lg text-[#0A1930]">{f.label}</h3>
                            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Audience badge */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-2 text-center">
                <span className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Built for everyone</span>
                <h2 className="font-display text-3xl sm:text-5xl font-semibold mt-3 text-[#0A1930] tracking-tight max-w-3xl mx-auto leading-tight">
                    One platform. <span className="text-[#7000FF]">Two audiences.</span> <span className="text-[#0066FF]">Three apps.</span>
                </h2>
                <p className="mt-4 text-base text-slate-600 max-w-2xl mx-auto">
                    Whether you're an individual unlocking your first nano-loan or an MSME owner running a digital business — OnePassCredit has a dedicated experience built for you.
                </p>
            </section>

            {/* For Individuals */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-2 gap-12 items-center">
                <div className="order-2 lg:order-1">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-purple-50 text-purple-700 border border-purple-200">
                        <User size={12} /> For Individuals
                    </div>
                    <h3 className="font-display text-3xl sm:text-4xl font-semibold mt-5 text-[#0A1930] tracking-tight">
                        Your money, your credit, your life — in one app
                    </h3>
                    <p className="mt-4 text-slate-600 leading-relaxed">
                        Build your Credit DNA Score™, manage your OnePass Wallet, and access progressive credit limits as you grow.
                        Designed for salaried workers, gig workers, students, and households across Africa.
                    </p>
                    <div className="mt-6 space-y-3">
                        {CONSUMER_PRODUCTS.map((p) => (
                            <div key={p.name} className="flex items-start gap-3" data-testid={`consumer-product-${p.name.replace(/[^a-zA-Z]/g, "")}`}>
                                <div className="size-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${p.color}15`, color: p.color }}>
                                    <p.icon size={18} />
                                </div>
                                <div>
                                    <div className="font-display font-semibold text-[#0A1930]">{p.name}</div>
                                    <div className="text-sm text-slate-600">{p.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-7 flex flex-wrap gap-3">
                        <QuickLoginButton
                            email="amina@onepass.demo"
                            label="Try as Amina (Consumer)"
                            role="consumer"
                            color="#7000FF"
                            icon={User}
                            testid="audience-quick-consumer"
                        />
                        <Link to="/register?role=consumer">
                            <Button variant="outline" className="font-semibold rounded-xl h-11 px-5 border-2 border-purple-200 text-purple-700 hover:bg-purple-50" data-testid="audience-register-consumer">
                                Create account
                            </Button>
                        </Link>
                    </div>
                </div>

                <div className="order-1 lg:order-2 relative">
                    <div className="absolute -inset-6 bg-gradient-to-br from-purple-200/40 via-violet-200/30 to-transparent rounded-[3rem] blur-2xl pointer-events-none" />
                    <img
                        src={DETAILED_MOCKUP}
                        alt="Individual app screens — Credit DNA, Wallet, Loans, Activity, Profile"
                        className="relative w-full rounded-3xl shadow-2xl"
                        data-testid="consumer-mockup"
                        style={{ objectFit: "cover", objectPosition: "top", maxHeight: 540 }}
                    />
                </div>
            </section>

            {/* For Businesses (MSMEs) */}
            <section className="bg-gradient-to-b from-slate-50 to-white border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-2 gap-12 items-center">
                    <div className="relative">
                        <div className="absolute -inset-6 bg-gradient-to-br from-blue-200/40 via-cyan-200/30 to-transparent rounded-[3rem] blur-2xl pointer-events-none" />
                        <img
                            src={DETAILED_MOCKUP}
                            alt="Business app screens — MerchantOS, BizCredit, Cashflow, Invoices"
                            className="relative w-full rounded-3xl shadow-2xl"
                            data-testid="business-mockup"
                            style={{ objectFit: "cover", objectPosition: "center", maxHeight: 540 }}
                        />
                    </div>
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-200">
                            <Briefcase size={12} /> For MSMEs / Businesses
                        </div>
                        <h3 className="font-display text-3xl sm:text-4xl font-semibold mt-5 text-[#0A1930] tracking-tight">
                            A complete operating system for your business
                        </h3>
                        <p className="mt-4 text-slate-600 leading-relaxed">
                            Run your sales, inventory, customers, suppliers, invoices, and expenses from one app — and unlock
                            working capital based on your real cashflow signals. From street stalls to logistics fleets to farms.
                        </p>
                        <div className="mt-6 space-y-3">
                            {BUSINESS_PRODUCTS.map((p) => (
                                <div key={p.name} className="flex items-start gap-3" data-testid={`business-product-${p.name.replace(/[^a-zA-Z]/g, "")}`}>
                                    <div className="size-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${p.color}15`, color: p.color }}>
                                        <p.icon size={18} />
                                    </div>
                                    <div>
                                        <div className="font-display font-semibold text-[#0A1930]">{p.name}</div>
                                        <div className="text-sm text-slate-600">{p.desc}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* MerchantOS sub-badge */}
                        <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-5 product-shadow">
                            <div className="flex items-center gap-2 mb-3">
                                <Layers size={16} className="text-[#0066FF]" />
                                <span className="text-xs uppercase tracking-wider font-bold text-slate-700">MerchantOS™ — built into the Business app</span>
                            </div>
                            <div className="grid grid-cols-4 gap-2">
                                {MERCHANT_TOOLS.map((t) => (
                                    <div key={t.label} className="flex flex-col items-center gap-1 p-2" data-testid={`mtool-${t.label.replace(/\s/g, "")}`}>
                                        <div className="size-9 rounded-lg bg-slate-50 text-slate-700 flex items-center justify-center">
                                            <t.icon size={16} />
                                        </div>
                                        <span className="text-[10px] font-semibold text-slate-600 text-center leading-tight">{t.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="mt-7 flex flex-wrap gap-3">
                            <QuickLoginButton
                                email="john@brightfoods.demo"
                                label="Try as John (MSME Owner)"
                                role="business"
                                color="#0066FF"
                                icon={Briefcase}
                                testid="audience-quick-business"
                            />
                            <Link to="/register?role=business">
                                <Button variant="outline" className="font-semibold rounded-xl h-11 px-5 border-2 border-blue-200 text-blue-700 hover:bg-blue-50" data-testid="audience-register-business">
                                    Create business account
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Admin Operations */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-2 gap-12 items-center">
                <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-slate-900 text-white">
                        <Building2 size={12} /> For Operations
                    </div>
                    <h3 className="font-display text-3xl sm:text-4xl font-semibold mt-5 text-[#0A1930] tracking-tight">
                        Run a full credit operation from one admin portal
                    </h3>
                    <p className="mt-4 text-slate-600 leading-relaxed">
                        Real-time portfolio dashboards, loan decisioning, collections workflows, fraud signals, and the Credit Engine rule editor — purpose-built for risk officers, credit officers, and management.
                    </p>
                    <div className="mt-5 grid grid-cols-2 gap-2">
                        {ADMIN_TOOLS.map((tool) => (
                            <div key={tool} className="flex items-center gap-2 text-sm text-slate-700" data-testid={`admin-tool-${tool.replace(/\s/g, "")}`}>
                                <div className="size-1.5 rounded-full bg-[#0066FF]" />
                                {tool}
                            </div>
                        ))}
                    </div>
                    <div className="mt-7">
                        <QuickLoginButton
                            email="admin@onepass.demo"
                            label="Open Admin Portal"
                            role="admin"
                            color="#0A1930"
                            icon={Building2}
                            testid="audience-quick-admin"
                        />
                    </div>
                </div>

                <div className="relative">
                    <div className="absolute -inset-6 bg-gradient-to-br from-slate-200/40 to-blue-200/30 rounded-[3rem] blur-2xl pointer-events-none" />
                    <img
                        src={CONSUMER_MOCKUP}
                        alt="Admin portal — dashboard, loans, risk, collections"
                        className="relative w-full rounded-3xl shadow-2xl"
                        data-testid="admin-mockup"
                    />
                </div>
            </section>

            <footer className="border-t border-slate-200 py-6 text-center text-xs text-slate-500">
                © 2026 OnePassCredit. Smart Credit. Simple Process. Stronger Personal Finance. Stronger Business.
            </footer>
        </div>
    );
}
